import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface PrintFormat {
  id: string;
  name: string;
  description?: string;
  documentType: string;
  htmlTemplate: string;
  cssStyles?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
}

const DOCUMENT_TYPES = [
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'PURCHASE_INVOICE', label: 'Purchase Invoice' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'PAYMENT', label: 'Payment' },
];

const PrintFormats: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [printFormats, setPrintFormats] = useState<PrintFormat[]>([]);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<PrintFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterDocumentType, setFilterDocumentType] = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    documentType: 'INVOICE',
    htmlTemplate: '',
    cssStyles: '',
    isDefault: false,
  });

  useEffect(() => {
    fetchPrintFormats();
  }, [filterDocumentType]);

  useEffect(() => {
    if (formData.documentType) {
      fetchPlaceholders(formData.documentType);
    }
  }, [formData.documentType]);

  const fetchPrintFormats = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (filterDocumentType) {
        params.documentType = filterDocumentType;
      }
      const response = await axios.get('/api/erp/print-formats', { params });
      setPrintFormats(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching print formats:', err);
      toast.error('Failed to load print formats');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaceholders = async (documentType: string) => {
    try {
      const response = await axios.get(`/api/erp/print-formats/placeholders/${documentType}`);
      setPlaceholders(response.data.data || []);
    } catch (err) {
      console.error('Error fetching placeholders:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.post('/api/erp/print-formats', formData);
      if (response.data.success) {
        toast.success('Print format created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchPrintFormats();
      } else {
        setError(response.data.error || 'Failed to create print format');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create print format';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleEdit = (format: PrintFormat) => {
    setSelectedFormat(format);
    setFormData({
      name: format.name,
      description: format.description || '',
      documentType: format.documentType,
      htmlTemplate: format.htmlTemplate,
      cssStyles: format.cssStyles || '',
      isDefault: format.isDefault,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFormat) return;
    setError(null);

    try {
      const response = await axios.put(`/api/erp/print-formats/${selectedFormat.id}`, formData);
      if (response.data.success) {
        toast.success('Print format updated successfully');
        setShowEditModal(false);
        setSelectedFormat(null);
        resetForm();
        fetchPrintFormats();
      } else {
        setError(response.data.error || 'Failed to update print format');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update print format';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this print format?')) return;

    try {
      const response = await axios.delete(`/api/erp/print-formats/${id}`);
      if (response.data.success) {
        toast.success('Print format deleted successfully');
        fetchPrintFormats();
      } else {
        toast.error(response.data.error || 'Failed to delete print format');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete print format');
    }
  };

  const handlePreview = (format: PrintFormat) => {
    setSelectedFormat(format);
    setShowPreviewModal(true);
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById('htmlTemplate') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + `{{${placeholder}}}` + after;
      setFormData({ ...formData, htmlTemplate: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length + 4, start + placeholder.length + 4);
      }, 0);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      documentType: 'INVOICE',
      htmlTemplate: '',
      cssStyles: '',
      isDefault: false,
    });
  };

  const getDefaultTemplate = (documentType: string): string => {
    if (documentType === 'INVOICE') {
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .company-info h1 { font-size: 24px; margin-bottom: 10px; }
    .invoice-info h2 { font-size: 28px; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background-color: #f5f5f5; }
    .totals { text-align: right; margin-top: 20px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-info">
      <h1>{{company.name}}</h1>
      <p>{{company.address}}</p>
      <p>{{company.city}}, {{company.state}} {{company.postalCode}}</p>
    </div>
    <div class="invoice-info">
      <h2>INVOICE</h2>
      <p>Invoice #: {{document.number}}</p>
      <p>Date: {{document.date}}</p>
      <p>Due Date: {{document.dueDate}}</p>
    </div>
  </div>
  
  <div>
    <h3>Bill To:</h3>
    <p>{{client.name}}</p>
    <p>{{client.address}}</p>
  </div>
  
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Quantity</th>
        <th>Unit Price</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      <!-- Items will be rendered here -->
    </tbody>
  </table>
  
  <div class="totals">
    <p>Subtotal: {{document.subtotal}}</p>
    <p>Tax: {{document.taxAmount}}</p>
    <p><strong>Total: {{document.total}}</strong></p>
  </div>
  
  {{#if document.notes}}
  <div>
    <h3>Notes:</h3>
    <p>{{document.notes}}</p>
  </div>
  {{/if}}
</body>
</html>`;
    }
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
  </style>
</head>
<body>
  <h1>{{document.number}}</h1>
  <p>Date: {{document.date}}</p>
  <p>Total: {{document.total}}</p>
</body>
</html>`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading print formats...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Print Formats</h1>
          <PageHelp articleKey="erp_print_format" />
        </div>
        <div>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage custom print formats for documents
          </p>
        </div>
        {hasPermission('erp_financial_report', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            New Print Format
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Document Type</label>
          <select
            value={filterDocumentType}
            onChange={(e) => setFilterDocumentType(e.target.value)}
            className="block w-full max-w-xs border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Types</option>
            {DOCUMENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {printFormats.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No print formats found. Create your first format to get started.</p>
          {hasPermission('erp_financial_report', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Print Format
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {printFormats.map(format => (
                <tr key={format.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{format.name}</div>
                    {format.description && (
                      <div className="text-sm text-gray-500">{format.description}</div>
                    )}
                    {format.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {DOCUMENT_TYPES.find(t => t.value === format.documentType)?.label || format.documentType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      format.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {format.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handlePreview(format)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Preview
                    </button>
                    {hasPermission('erp_financial_report', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(format)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_financial_report', 'delete') && (
                          <button
                            onClick={() => handleDelete(format.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Print Format</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Type *</label>
                  <select
                    required
                    value={formData.documentType}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        documentType: e.target.value,
                        htmlTemplate: getDefaultTemplate(e.target.value)
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label className="ml-2 text-sm text-gray-700">Set as default format for this document type</label>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">HTML Template *</label>
                  <div className="text-sm text-gray-500">
                    Available placeholders:
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder)}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="htmlTemplate"
                  required
                  value={formData.htmlTemplate}
                  onChange={(e) => setFormData({ ...formData, htmlTemplate: e.target.value })}
                  rows={20}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter HTML template with {{placeholders}}"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Custom CSS Styles</label>
                <textarea
                  value={formData.cssStyles}
                  onChange={(e) => setFormData({ ...formData, cssStyles: e.target.value })}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter custom CSS styles"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Format
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar to Create */}
      {showEditModal && selectedFormat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Print Format</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedFormat(null); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Type *</label>
                  <select
                    required
                    value={formData.documentType}
                    onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label className="ml-2 text-sm text-gray-700">Set as default format for this document type</label>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">HTML Template *</label>
                  <div className="text-sm text-gray-500">
                    Available placeholders:
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder)}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="htmlTemplate"
                  required
                  value={formData.htmlTemplate}
                  onChange={(e) => setFormData({ ...formData, htmlTemplate: e.target.value })}
                  rows={20}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Custom CSS Styles</label>
                <textarea
                  value={formData.cssStyles}
                  onChange={(e) => setFormData({ ...formData, cssStyles: e.target.value })}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedFormat(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Update Format
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedFormat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Preview: {selectedFormat.name}</h3>
              <button onClick={() => { setShowPreviewModal(false); setSelectedFormat(null); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <div className="border rounded p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-2">HTML Template:</div>
              <pre className="bg-white p-4 rounded border overflow-auto text-xs max-h-96">
                {selectedFormat.htmlTemplate}
              </pre>
            </div>
            {selectedFormat.cssStyles && (
              <div className="border rounded p-4 bg-gray-50 mt-4">
                <div className="text-sm text-gray-600 mb-2">CSS Styles:</div>
                <pre className="bg-white p-4 rounded border overflow-auto text-xs max-h-96">
                  {selectedFormat.cssStyles}
                </pre>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => { setShowPreviewModal(false); setSelectedFormat(null); }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintFormats;
